import './CaseStudyDetail.scss'

//@ts-ignore
import Layout from '@componentsfld/core/Layout'
import { HeadProps } from 'gatsby'
import React, { FunctionComponent } from 'react'
import { IPageContext } from 'Root/Shared/utils/data/buildTypedPage'
import { PuxVWO } from 'Root/Shared/utils/PuxVWO'
import WidgetBuilder from 'Shared/components/builders/WidgetBuilder'
import PuxMediaSelector from 'Shared/components/content/mediaImage/PuxMediaSelector'
import EasySoftwareBreadcrumbs from 'Shared/components/EasySoftwareBreadcrumbs/EasySoftwareBreadcrumbs'
import EasySoftwareEditorContainer from 'Shared/components/EasySoftwareEditorContainer/EasySoftwareEditorContainer'
import PuxIcon from 'Shared/components/PuxIcon/PuxIcon'
import {
  GenericPageProps,
  IEasySoftwareCaseStudy,
} from 'Shared/queries/page-queries'
import { getUrlWithProtocol } from 'Shared/utils/getUrlWithProtocol'
import PuxMetaTagsGatsby from 'Shared/utils/PuxMetaTagsGatsby'
import { t } from 'ttag'
import { getLocalizedData, getLocalizedUrl } from 'Root/Shared/utils/localeURL'

const localizedConfigData = getLocalizedData(
  process.env.LOCALE ?? ``,
  process.env.PROJECT ?? ``
)
const activeEnv = process.env.GATSBY_ACTIVE_ENV ?? ``

function getCaseStudyDetailJson(pageData: IEasySoftwareCaseStudy, metaTags: any) {
  const baseUrl = localizedConfigData.domains[activeEnv]
  const fullUrl = baseUrl + getLocalizedUrl(pageData.path)
  const productName = "Easy8"

  // Clean HTML from content
  const cleanPerex = pageData.caseStudyPerex?.html?.replace(/<[^>]*>/g, '') || ''
  const cleanContent = pageData.caseStudyContent?.html?.replace(/<[^>]*>/g, '') || ''
  const description = metaTags?.metaTagsDescription?.html?.replace(/<[^>]*>/g, '') || cleanPerex

  // Get image URL - prefer top image, fallback to regular image
  const imageData = pageData.caseStudyTopImage?.mediaSelectorImage || pageData.caseStudyImage?.mediaSelectorImage
  const imageUrl = imageData?.resizePaths?.[0]
    || imageData?.resizePathsFallback?.[0]
    || ''
  const fullImageUrl = imageUrl.startsWith('http') ? imageUrl : `${baseUrl}${imageUrl}`

  // Format date
  const date = new Date(pageData.createdUtc)
  const datePublished = date.toISOString().split('T')[0]
  const dateModified = new Date(pageData.caseStudyUpdated || pageData.createdUtc).toISOString().split('T')[0]

  // Build article body from parameters, perex and content
  let articleBody = cleanPerex
  if (pageData.caseStudyParameters?.contentItems) {
    const parametersText = pageData.caseStudyParameters.contentItems
      .map(param => {
        const value = param.keyValueContent?.html?.replace(/<[^>]*>/g, '') || ''
        return `${param.displayText}: ${value}`
      })
      .join('. ')
    articleBody = `${parametersText}. ${cleanPerex}. ${cleanContent}`
  } else {
    articleBody = `${cleanPerex}. ${cleanContent}`
  }

  const structuredData: any = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": fullUrl,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": fullUrl
    },
    "headline": pageData.displayText,
    "description": description,
    "articleBody": articleBody,
    "author": {
      "@type": "Person",
      "name": productName
    },
    "publisher": {
      "@type": "Organization",
      "name": productName,
      "url": baseUrl
    },
    "datePublished": datePublished,
    "dateModified": dateModified,
    "image": {
      "@type": "ImageObject",
      "url": fullImageUrl,
      "width": 1200,
      "height": 630
    },
    "articleSection": "Case Study",
    "inLanguage": localizedConfigData.isoCode || "en"
  }

  // Add "about" field if caseStudyUrl exists
  if (pageData.caseStudyUrl) {
    structuredData.about = {
      "@type": "Thing",
      "url": getUrlWithProtocol(pageData.caseStudyUrl) || pageData.caseStudyUrl
    }
  }

  return structuredData
}

const CaseStudyDetail: FunctionComponent<
  GenericPageProps<IEasySoftwareCaseStudy>
> = (props) => {
  const pageData = props.pageContext.pageData
  const breadcrumbsData = props.pageContext.breadcrumbs
  const headerData = props.pageContext.headerData
  const footerData = props.pageContext.footerData
  const metaTags = props.pageContext.metaTags
  const widgets = props.pageContext.widgets
  const caseStudyParameters = pageData.caseStudyParameters?.contentItems ?? []
  const hasTopImage =
    (pageData?.caseStudyTopImage?.mediaSelectorImage?.resizePaths?.length ??
      0) > 0
  const editorPath = `caseStudyWidgets,widgets`
  const date = new Date(pageData.createdUtc)
  const caseStudyUrl = getUrlWithProtocol(pageData.caseStudyUrl)

  let dateUpdated: Date | null = null

  if (pageData.caseStudyUpdated) {
    dateUpdated = new Date(pageData.caseStudyUpdated)
  }

  const heroDate = dateUpdated ?? date

  return (
    <Layout
      localizedPath={pageData.localization?.localizations}
      footerData={footerData}
      headerData={headerData}
      pageContentItemId={pageData.contentItemId}
      pageProperties={props.pageContext.pageProperties}
      resStrings={props.pageContext.resStrings}
    >
      <div className='CaseStudyDetail'>
        <EasySoftwareBreadcrumbs items={breadcrumbsData} />
        {hasTopImage ? (
          <div className='CaseStudyDetail-topimage'>
            <div className='CaseStudyDetail-hero'>
              <div className='CaseStudyDetail-heroText'>
                <div className='CaseStudyDetail-heroLogo'>
                  <PuxMediaSelector
                    {...pageData.caseStudyImage}
                    width={280}
                    height={63}
                    disableLazyload
                  />
                </div>
                <div className='CaseStudyDetail-date'>
                  <PuxIcon
                    name='user-interface/calendar'
                    size={12}
                    decorative
                  />
                  {heroDate.toLocaleDateString()}
                </div>
                <h1 className='CaseStudyDetail-title'>
                  {pageData.displayText}
                </h1>
                {pageData.caseStudyUrl && caseStudyUrl ? (
                  <a
                    className='CaseStudyDetail-heroButton'
                    href={caseStudyUrl}
                    target='_blank'
                    rel='noopener'
                  >
                    {t`EasyRedmine.CaseStudyDetail.WebsiteButtonText`}
                  </a>
                ) : null}
              </div>
              <div className='CaseStudyDetail-heroVisual'>
                <PuxMediaSelector
                  {...pageData.caseStudyTopImage!}
                  width={610}
                  height={610}
                  disableLazyload
                />
              </div>
            </div>
          </div>
        ) : null}
        <div className='CaseStudyDetail-container'>
          {!hasTopImage && (
            <>
              <h1 className='CaseStudyDetail-title'>{pageData.displayText}</h1>
              <div className='CaseStudyDetail-date'>
                {date.toLocaleDateString()}
              </div>
            </>
          )}
          {caseStudyParameters.length > 0 ? (
            <div className='CaseStudyDetail-meta'>
              <div className='CaseStudyDetail-parameters'>
                {caseStudyParameters.map((item) => (
                  <div
                    className='CaseStudyDetail-parameter'
                    key={item.contentItemId}
                  >
                    <div className='CaseStudyDetail-label'>
                      {item.displayText}:
                    </div>
                    <div className='CaseStudyDetail-value'>
                      <EasySoftwareEditorContainer
                        pageContentItemId={item.contentItemId}
                        inline
                        content={item.keyValueContent}
                        editorPath={`caseStudyParameters,contentItems,cid-${item.contentItemId},puxKeyValue,keyValueContent,html`} // TODO KAJA
                        wysiwygId={item.contentItemId}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className='CaseStudyDetail-perex perex'>
            <EasySoftwareEditorContainer
              pageContentItemId={pageData.contentItemId}
              content={pageData.caseStudyPerex}
              editorPath={`caseStudyPerex,html`}
              wysiwygId={pageData.contentItemId + `-perex`}
            />
          </div>

          <div className='CaseStudyDetail-content'>
            <EasySoftwareEditorContainer
              pageContentItemId={pageData.contentItemId}
              content={pageData.caseStudyContent}
              editorPath={`caseStudyContent,html`}
              wysiwygId={pageData.contentItemId + `-content`}
            />
          </div>
        </div>
      </div>
      <div className='CaseStudyDetail-widgetContainer'>
        <WidgetBuilder
          pageContentItemId={pageData.contentItemId}
          widgetBuilderData={widgets?.caseStudyWidgets}
          editorPath={editorPath}
        />
      </div>
    </Layout>
  )
}

export default CaseStudyDetail

export function Head(
  props: HeadProps<never, IPageContext<IEasySoftwareCaseStudy>>
) {
  const pageData = props.pageContext.pageData
  const metaTags = props.pageContext.metaTags

  return (
    <>
      <PuxMetaTagsGatsby
        {...metaTags}
        localizedPaths={pageData.localization?.localizations}
        structuredData={getCaseStudyDetailJson(pageData, metaTags)}
      />
      <PuxVWO isEnabled={props.pageContext.pageProperties?.pagePropertiesVWOEnabled} />
    </>
  )
}
